import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Webhook, Zap, Building2, Mail, MessageSquare,
  FileSpreadsheet, Users, CheckCircle, ExternalLink, Search,
  ArrowRight, Sparkles, Clock, Lock, TrendingUp
} from 'lucide-react';
import styles from './IntegrationMarketplace.module.css';

export default function IntegrationMarketplace() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleRequestIntegration = () => {
    const subject = encodeURIComponent('Integration Request for Vennio');
    const body = encodeURIComponent(
      'Hi Vennio Team,\n\nI would like to request an integration with:\n\nIntegration Name: \nUse Case: \nPriority (High/Medium/Low): \n\nAdditional Details:\n\n\nThanks!'
    );
    window.location.href = `mailto:integrations@vennio.com?subject=${subject}&body=${body}`;
  };

  const handleNotifyMe = (integrationName) => {
    const subject = encodeURIComponent(`Notify me: ${integrationName} Integration`);
    const body = encodeURIComponent(
      `Hi Vennio Team,\n\nPlease notify me when the ${integrationName} integration is available.\n\nName: \nEmail: \nCompany: \n\nThanks!`
    );
    window.location.href = `mailto:integrations@vennio.com?subject=${subject}&body=${body}`;
  };

  const categories = [
    { id: 'all', name: 'All Integrations' },
    { id: 'calendar', name: 'Calendar' },
    { id: 'crm', name: 'CRM' },
    { id: 'automation', name: 'Automation' },
    { id: 'communication', name: 'Communication' },
    { id: 'productivity', name: 'Productivity' }
  ];

  const liveIntegrations = [
    {
      id: 'google-workspace', name: 'Google Workspace',
      description: 'Sync availability and manage bookings with Google Calendar',
      icon: Calendar, category: 'calendar', status: 'live',
      features: ['OAuth 2.0 authentication', 'Multi-calendar support', 'Real-time availability sync', 'Automatic meeting creation', 'Calendar event management'],
      setupTime: '2 minutes', pricing: 'Free', docsUrl: '/docs/integrations/google-workspace', connectUrl: '/dashboard'
    },
    {
      id: 'hubspot', name: 'HubSpot',
      description: 'Automatically sync meetings and contacts to your HubSpot CRM',
      icon: Building2, category: 'crm', status: 'live',
      features: ['Auto-create contacts from bookings', 'Sync meetings to CRM timeline', 'Link meetings to contacts', 'Meeting notes and details', 'OAuth 2.0 secure connection'],
      setupTime: '5 minutes', pricing: 'Free', docsUrl: '/docs/integrations/hubspot', connectUrl: '/dashboard'
    },
    {
      id: 'zapier', name: 'Zapier',
      description: 'Connect Vennio to 1000+ apps with automated workflows',
      icon: Zap, category: 'automation', status: 'live',
      features: ['Connect to 1000+ apps', 'Real-time webhook triggers', 'Google Sheets, Slack, Email', 'CRM integrations', 'Custom workflows'],
      setupTime: '5 minutes', pricing: 'Free', docsUrl: '/docs/zapier-quickstart', connectUrl: '/dashboard'
    }
  ];

  const comingSoonIntegrations = [
    { id: 'salesforce', name: 'Salesforce', description: 'Sync meetings and contacts with Salesforce CRM', icon: Building2, category: 'crm', status: 'coming-soon', eta: 'Q1 2026' },
    { id: 'microsoft-365', name: 'Microsoft 365', description: 'Integrate with Outlook Calendar and Teams', icon: Calendar, category: 'calendar', status: 'coming-soon', eta: 'Q1 2026' },
    { id: 'slack', name: 'Slack', description: 'Send booking notifications to Slack channels', icon: MessageSquare, category: 'communication', status: 'coming-soon', eta: 'Q2 2026' },
    { id: 'mailchimp', name: 'Mailchimp', description: 'Add customers to email lists automatically', icon: Mail, category: 'communication', status: 'coming-soon', eta: 'Q2 2026' },
    { id: 'notion', name: 'Notion', description: 'Log bookings and customer data to Notion databases', icon: FileSpreadsheet, category: 'productivity', status: 'coming-soon', eta: 'Q2 2026' },
    { id: 'pipedrive', name: 'Pipedrive', description: 'Manage deals and contacts with booking data', icon: TrendingUp, category: 'crm', status: 'coming-soon', eta: 'Q2 2026' }
  ];

  const filteredLive = liveIntegrations.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch && (selectedCategory === 'all' || i.category === selectedCategory);
  });

  const filteredComingSoon = comingSoonIntegrations.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch && (selectedCategory === 'all' || i.category === selectedCategory);
  });

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBadge}>
            <Sparkles size={16} aria-hidden="true" />
            Infrastructure for Time
          </div>
          <h1 className={styles.heroTitle}>Integration Marketplace</h1>
          <p className={styles.heroSubtitle}>
            Connect Vennio to your existing tools. Build your scheduling infrastructure with the apps you already use.
          </p>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <CheckCircle size={20} aria-hidden="true" />
              <span>{liveIntegrations.length} Live Integrations</span>
            </div>
            <div className={styles.heroStat}>
              <Clock size={20} aria-hidden="true" />
              <span>{comingSoonIntegrations.length} Coming Soon</span>
            </div>
            <div className={styles.heroStat}>
              <Lock size={20} aria-hidden="true" />
              <span>Enterprise Security</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.main}>
        {/* Search + filter */}
        <div className={styles.searchRow}>
          <div className={styles.searchWrap}>
            <Search size={20} className={styles.searchIcon} aria-hidden="true" />
            <input
              type="text"
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.categoryFilter}>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`${styles.categoryBtn} ${selectedCategory === cat.id ? styles.categoryBtnActive : ''}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Live integrations */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <CheckCircle size={28} className={styles.sectionIconSuccess} aria-hidden="true" />
            <h2 className={styles.sectionTitle}>Live Integrations</h2>
          </div>

          {filteredLive.length === 0 ? (
            <div className={styles.noResults}>
              <Search size={48} className={styles.noResultsIcon} aria-hidden="true" />
              <p>No integrations found matching your search.</p>
            </div>
          ) : (
            <div className={styles.cardsGrid}>
              {filteredLive.map(integration => (
                <IntegrationCard key={integration.id} integration={integration} />
              ))}
            </div>
          )}
        </div>

        {/* Coming soon */}
        {filteredComingSoon.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Clock size={28} className={styles.sectionIconAccent} aria-hidden="true" />
              <h2 className={styles.sectionTitle}>Coming Soon</h2>
            </div>
            <div className={styles.cardsGrid}>
              {filteredComingSoon.map(integration => (
                <ComingSoonCard key={integration.id} integration={integration} onNotifyMe={handleNotifyMe} />
              ))}
            </div>
          </div>
        )}

        {/* Request CTA */}
        <div className={styles.cta}>
          <h3 className={styles.ctaTitle}>Don't see your integration?</h3>
          <p className={styles.ctaBody}>
            We're constantly adding new integrations. Let us know which tools you'd like to connect and we'll prioritize based on demand.
          </p>
          <button onClick={handleRequestIntegration} className={styles.ctaBtn}>
            Request an Integration
            <ArrowRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ integration }) {
  const Icon = integration.icon;

  return (
    <div className={styles.integrationCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIconWrap}>
            <Icon size={24} className={styles.cardIcon} aria-hidden="true" />
          </div>
          <div>
            <h3 className={styles.cardName}>{integration.name}</h3>
            <span className={styles.liveBadge}>
              <CheckCircle size={12} aria-hidden="true" />
              Live
            </span>
          </div>
        </div>
      </div>

      <p className={styles.cardDesc}>{integration.description}</p>

      <div className={styles.features}>
        <p className={styles.featuresLabel}>Key Features</p>
        <ul className={styles.featuresList}>
          {integration.features.slice(0, 3).map((feature, i) => (
            <li key={i} className={styles.featureItem}>
              <CheckCircle size={16} className={styles.featureCheck} aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.cardMeta}>
        <div className={styles.cardMetaItem}>
          <Clock size={14} aria-hidden="true" />
          <span>{integration.setupTime} setup</span>
        </div>
        <span className={styles.cardPricing}>{integration.pricing}</span>
      </div>

      <div className={styles.cardActions}>
        <Link to={integration.connectUrl} className={styles.connectBtn}>Connect</Link>
        <a
          href={integration.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.docsBtn}
        >
          Docs
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

function ComingSoonCard({ integration, onNotifyMe }) {
  const Icon = integration.icon;

  return (
    <div className={styles.comingSoonCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIconWrapMuted}>
            <Icon size={24} className={styles.cardIconMuted} aria-hidden="true" />
          </div>
          <div>
            <h3 className={styles.cardName}>{integration.name}</h3>
            <span className={styles.etaBadge}>
              <Clock size={12} aria-hidden="true" />
              {integration.eta}
            </span>
          </div>
        </div>
      </div>

      <p className={styles.cardDesc}>{integration.description}</p>

      <button onClick={() => onNotifyMe(integration.name)} className={styles.notifyBtn}>
        Notify Me
      </button>
    </div>
  );
}
